/**
 * CEO Command Center - API Client Service
 *
 * Secure API client that communicates with the Cloudflare Worker proxy.
 * All API keys are stored server-side - never exposed to the browser.
 *
 * Features:
 * - Automatic retry with exponential backoff
 * - Request timeout handling
 * - Error normalization
 * - Response caching headers
 * - Comprehensive logging
 *
 * NOTE: v11.0.0 - Airtable has been deprecated. All data is now stored locally
 * in SQLite via CR-SQLite. This client only handles OpenAI and NewsAPI proxying.
 *
 * @module services/api/client
 * @version 11.0.0
 */

import { API_CONFIG } from '../../config/constants';
import type {
  SignalsResponse,
} from '../../types';

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for API errors
 * Provides structured error information
 */
export class APIError extends Error {
  public readonly status: number;
  public readonly data: unknown;
  
  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
  
  /**
   * Check if error is a network error (no response)
   */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
  
  /**
   * Check if error is a timeout
   */
  get isTimeout(): boolean {
    return this.status === 408;
  }
  
  /**
   * Check if error is a rate limit
   */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
  
  /**
   * Check if error is retryable
   */
  get isRetryable(): boolean {
    return this.status === 429 || 
           this.status === 502 || 
           this.status === 503 || 
           this.status === 504 ||
           this.status === 0;
  }
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Sleep for a specified duration
 */
const sleep = (ms: number): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 */
const getRetryDelay = (attempt: number): number => {
  const delay = API_CONFIG.RETRY.BASE_DELAY_MS * Math.pow(2, attempt);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * delay;
  return Math.min(delay + jitter, API_CONFIG.RETRY.MAX_DELAY_MS);
};

// ============================================
// BASE API CLIENT
// ============================================

/**
 * Make an API request with retry logic
 * Routes requests through the Cloudflare Worker proxy.
 *
 * NOTE: v11.0.0 - Only used for OpenAI and NewsAPI. Data operations use SQLite.
 *
 * @param endpoint - API endpoint (e.g., '/api/openai/chat')
 * @param options - Fetch options
 * @returns Parsed JSON response
 * @throws APIError on failure
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  const fetchOptions: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include', // Include cookies for CORS
  };
  
  let lastError: APIError | null = null;
  
  for (let attempt = 0; attempt < API_CONFIG.RETRY.MAX_ATTEMPTS; attempt++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(), 
        API_CONFIG.TIMEOUT
      );
      
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // Parse response
      const data = await response.json();
      
      // Handle HTTP errors
      if (!response.ok) {
        const error = new APIError(
          data.error || `HTTP ${response.status}`,
          response.status,
          data
        );
        
        // Retry if appropriate
        if (error.isRetryable && attempt < API_CONFIG.RETRY.MAX_ATTEMPTS - 1) {
          lastError = error;
          const delay = getRetryDelay(attempt);
          console.warn(`API request failed (${response.status}), retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }
        
        throw error;
      }
      
      return data as T;
      
    } catch (error) {
      // Handle abort/timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new APIError('Request timeout', 408);
      }
      
      // Re-throw APIError
      if (error instanceof APIError) {
        throw error;
      }
      
      // Network error - retry
      if (attempt < API_CONFIG.RETRY.MAX_ATTEMPTS - 1) {
        lastError = new APIError(
          `Network error: ${error instanceof Error ? error.message : 'Unknown'}`,
          0
        );
        const delay = getRetryDelay(attempt);
        console.warn(`Network error, retrying in ${delay}ms...`);
        await sleep(delay);
        continue;
      }
      
      throw new APIError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown'}`,
        0
      );
    }
  }
  
  throw lastError || new APIError('Max retries exceeded', 500);
}

// ============================================
// SIGNALS SERVICE
// ============================================

/**
 * Signals service
 * Fetches server-computed signals
 */
export const signalsService = {
  /**
   * Fetch computed signals from server
   */
  async getSignals(): Promise<SignalsResponse> {
    return apiRequest<SignalsResponse>('/api/signals');
  },
};

// ============================================
// NEWS SERVICE
// ============================================

/**
 * News service
 * Fetches market intelligence
 */
export const newsService = {
  /**
   * Search for news articles
   */
  async search(query: string): Promise<{
    articles: Array<{
      title: string;
      source: { name: string };
      url: string;
      publishedAt: string;
      description: string;
    }>;
  }> {
    return apiRequest(`/api/news?q=${encodeURIComponent(query)}`);
  },
};

// ============================================
// HEALTH CHECK
// ============================================

/**
 * Check API health status
 */
export async function checkApiHealth(): Promise<{
  status: string;
  version: string;
  timestamp: string;
}> {
  return apiRequest('/api/health');
}

// ============================================
// EXPORT
// ============================================

export default {
  signals: signalsService,
  news: newsService,
  health: checkApiHealth,
  APIError,
};
